import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";

import AlertContext, { ALERT_STATUS } from "context/alert/alertContext";
import AuthContext from "context/auth/authContext";
import { useApi } from "shared/hooks/useApi";
import { ENDPOINT_PATH_SEGMENTS, ENDPOINTS } from "shared/types/enums";

import { DISCONNECTED } from "./constants";
import SignalRContext from "./signalRContext";
import { SignalRContextState, SignalRStateProps } from "./types/interfaces";

const SignalRState = ({ children }: SignalRStateProps) => {
    const { setAlert } = useContext(AlertContext);
    const { isAuthenticated } = useContext(AuthContext);

    const { t } = useTranslation();
    const { postData } = useApi();

    const [value, setValue] = useState<SignalRContextState>(DISCONNECTED);

    useEffect(() => {
        if (!isAuthenticated) return;

        const getSignalRAuthToken = async () => {
            const { data } = await postData(
                `${ENDPOINTS.Tokens}${ENDPOINT_PATH_SEGMENTS.RealtimeEventServiceToken}`
            );

            return data.token;
        };

        const connection = new HubConnectionBuilder()
            .withUrl(import.meta.env.VITE_SIGNALR_HUB_URL, {
                accessTokenFactory: getSignalRAuthToken,
                withCredentials: false
            })
            .withAutomaticReconnect()
            .build();

        let isActive = true;

        const update = () => {
            if (isActive) {
                setValue({ connection, state: connection.state });
            }
        };

        connection.onreconnecting(update);
        connection.onreconnected(update);
        connection.onclose(update);

        const connect = async () => {
            setValue({ connection, state: HubConnectionState.Connecting });

            try {
                await connection.start();
            } catch {
                if (!isActive) return;

                setAlert({
                    status: ALERT_STATUS.Critical,
                    title: t("Flash##signalr connection failed")
                });
            } finally {
                update();
            }
        };

        connect();

        return () => {
            isActive = false;
            connection.stop();
            setValue(DISCONNECTED);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    return <SignalRContext value={value}>{children}</SignalRContext>;
};

export default SignalRState;
